import { keyBy } from 'lodash';

import {
	IAzureClassificationNode,
	IAzureField,
	IAzureFields,
	IAzureFieldType,
	IAzureProject,
	IAzureUser,
	OptionType
} from '@evinced-private/ui-common';

import {
	ACCOUNTS_API_VERSION,
	ACCOUNTS_URL,
	BYPASS_RULES_QUERY_PARAM,
	BYPASS_RULES_QUERY_VALUE
} from '../consts/AzureConstants';
import { AZURE_URL_PATHS } from '../consts/AzureUrlPaths';
import {
	addAttachmentToPayload,
	dataURItoBlob,
	getClassificationNodes,
	getFieldsMetaData,
	getFormattedFields,
	getOrganizationNameUrl,
	getPaths,
	getProjectNameUrl,
	getProjectsOptionType,
	getProjectTypesOptions,
	getUsersUrl,
	openUrlInNewTab
} from '../helpers/AzureHelper';
import {
	AzureApiDeps,
	AzureAttachmentResponse,
	AzureGraphUser,
	AzureListResponse,
	AzureUserProfileResponse,
	AzureWorkItemResponse
} from '../types/AzureApi.types';
import { AzureLogger } from '../types/AzureTicketCreatorConfig';
import { IAzureOrganization, IAzureProjectType } from '../types/AzureTypes';

import { AzureAuthClient } from './azureAuth';

const {
	API_VERSION,
	WORK_ITEM_FIELDS,
	WORK_ITEM_TYPE,
	PROJECTS,
	WORK_ITEMS,
	PROJECT_TYPES,
	USERS,
	FIELDS,
	CLASSIFICATIONS,
	USER,
	ATTACHMENT
} = AZURE_URL_PATHS;

/**
 * Append a path (and optional `?query`) to an absolute Azure REST base URL using the {@link URL} API.
 *
 * Stateless helper kept as a module-level export: the {@link AzureApi} instance uses it internally
 * and the backward-compatibility contract test pins URL construction against it.
 */
const appendAzurePath = (baseHref: string, pathAndOptionalQuery: string): URL => {
	const url = new URL(baseHref);
	const qIndex = pathAndOptionalQuery.indexOf('?');
	const pathnamePart = qIndex >= 0 ? pathAndOptionalQuery.slice(0, qIndex) : pathAndOptionalQuery;
	const searchPart = qIndex >= 0 ? pathAndOptionalQuery.slice(qIndex + 1) : '';
	const root = url.pathname.replace(/\/$/, '');
	const suffix = pathnamePart.startsWith('/') ? pathnamePart : `/${pathnamePart}`;
	url.pathname = `${root}${suffix}`;
	if (searchPart) {
		new URLSearchParams(searchPart).forEach((value, key) => {
			url.searchParams.set(key, value);
		});
	}
	return url;
};

const mergeApiVersionParams = (url: URL): void => {
	new URLSearchParams(API_VERSION).forEach((value, key) => {
		url.searchParams.set(key, value);
	});
};

const getAzureWorkItemCreateRelativePath = (
	apiVersionQuery: string,
	workItemTypeName: string,
	bypassProcessRules = true
): string => {
	const params = new URLSearchParams();
	if (bypassProcessRules) {
		params.set(BYPASS_RULES_QUERY_PARAM, BYPASS_RULES_QUERY_VALUE);
	}
	const versionParams = new URLSearchParams(apiVersionQuery);
	versionParams.forEach((value, key) => {
		params.set(key, value);
	});
	const serializedQuery = params.toString();
	const resource = `${WORK_ITEMS}$${workItemTypeName}`;
	return serializedQuery === '' ? resource : `${resource}?${serializedQuery}`;
};

/**
 * Single entry point for all Azure DevOps REST calls. Built by the provider with an auth client
 * and logger injected — no module-level singleton or global config.
 */
export class AzureApi {
	private readonly auth: AzureAuthClient;

	private readonly logger: AzureLogger;

	private readonly apiBaseUrl: string;

	private readonly usersBaseUrl: string;

	constructor({ auth, logger, apiBaseUrl, usersBaseUrl }: AzureApiDeps) {
		this.auth = auth;
		this.logger = logger;
		this.apiBaseUrl = apiBaseUrl;
		this.usersBaseUrl = usersBaseUrl;
	}

	private async innerCallApi<T = unknown>(
		url: string,
		method = 'GET',
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		payload?: any,
		withToken = true,
		contentType = 'application/json-patch+json'
	): Promise<T> {
		const requestHeaders: HeadersInit = new Headers();

		requestHeaders.set('Content-Type', contentType);
		requestHeaders.set('Accept', 'application/json-patch+json');

		if (withToken) {
			try {
				requestHeaders.set('Authorization', `Bearer ${await this.auth.acquireAzureToken()}`);
			} catch (error) {
				this.logError('token acquisition', error);
				return Promise.reject(new Error('Unauthorized'));
			}
		}

		const fetchOptions: RequestInit = {
			mode: 'cors',
			method,
			headers: requestHeaders,
			body: null
		};

		if (payload) {
			fetchOptions.body =
				contentType === 'application/json-patch+json' ? JSON.stringify(payload) : payload;
		}
		this.logger.debug('request started: ', url);
		try {
			const response = await fetch(url, fetchOptions);

			if (!response.ok) {
				throw new Error(`Azure API request failed (${response.status}): ${url}`);
			}
			this.logger.debug('request complete successfully: ', url);
			const json = (await response.json()) as T;
			this.logger.debug('Response for ', url, 'is:', json);
			return json;
		} catch (err) {
			this.logError(`request ${method} ${url}`, err);
			throw err;
		}
	}

	/** Uniform error logging for Azure operations, via the injected logger. */
	private logError(operation: string, error: unknown): void {
		this.logger.error(`Azure ${operation} failed:`, error);
	}

	public async getProjects(host: string): Promise<IAzureProject[]> {
		const url = getOrganizationNameUrl(host, this.apiBaseUrl);
		try {
			const requestUrl = appendAzurePath(url, PROJECTS);
			mergeApiVersionParams(requestUrl);
			const projects = await this.innerCallApi<AzureListResponse<IAzureProject>>(requestUrl.href);
			return getProjectsOptionType(projects?.value) ?? [];
		} catch (error) {
			this.logError('getProjects', error);
			return [];
		}
	}

	private async getUsers(host: string): Promise<IAzureUser[]> {
		const url = getUsersUrl(host, this.usersBaseUrl);

		try {
			const requestUrl = appendAzurePath(url, USERS);
			const { value } = await this.innerCallApi<AzureListResponse<AzureGraphUser>>(requestUrl.href);
			// Drop the automated "Build Service" identities, then map onto IAzureUser.
			return value
				.filter((user) => !user.displayName.includes('Build Service'))
				.map((user) => ({
					id: user.originId,
					email: user.mailAddress,
					displayName: user.displayName
				}));
		} catch (error) {
			this.logError('getUsers', error);
			return [];
		}
	}

	private async getClassification(host: string): Promise<IAzureClassificationNode[]> {
		const requestUrl = appendAzurePath(host, `/${CLASSIFICATIONS}`);
		mergeApiVersionParams(requestUrl);
		try {
			const { value } = await this.innerCallApi<AzureListResponse<IAzureClassificationNode>>(
				requestUrl.href
			);
			return value;
		} catch (error) {
			this.logError('getClassification', error);
			return [];
		}
	}

	private async getSelectedIssueType(
		host: string,
		selectedIssueType: string
	): Promise<IAzureFieldType[]> {
		const requestUrl = appendAzurePath(
			host,
			`${WORK_ITEM_TYPE}/${selectedIssueType}${WORK_ITEM_FIELDS}`
		);
		mergeApiVersionParams(requestUrl);
		try {
			const { value } = await this.innerCallApi<AzureListResponse<IAzureFieldType>>(
				requestUrl.href
			);
			return value;
		} catch (error) {
			this.logError('getSelectedIssueType', error);
			return [];
		}
	}

	public async getProjectFields(host: string, selectedIssueType: string): Promise<IAzureFields> {
		const fieldsUrl = appendAzurePath(host, FIELDS.replace(/&$/, ''));
		mergeApiVersionParams(fieldsUrl);
		try {
			// These four reads are independent, so fetch them concurrently rather than in series.
			const [issueTypeFields, fieldsResponse, azureUsers, classificationNodes] = await Promise.all([
				this.getSelectedIssueType(host, selectedIssueType),
				this.innerCallApi<AzureListResponse<IAzureField>>(fieldsUrl.href),
				this.getUsers(host),
				this.getClassification(host)
			]);
			let selectedIssueTypeFields: IAzureFieldType[] = issueTypeFields;
			const fields: Record<string, IAzureField> = keyBy(fieldsResponse.value, 'referenceName');
			const users: string[] = azureUsers.map((user: IAzureUser) => user.displayName);
			// Select Area/Iteration by structureType — ADO does not guarantee positional order.
			const areaNodes = classificationNodes.filter((node) => node.structureType === 'area');
			const iterationsNodes = classificationNodes.filter(
				(node) => node.structureType === 'iteration'
			);
			const areas: string[] = getClassificationNodes(areaNodes);
			const iterations: string[] = getClassificationNodes(iterationsNodes);

			selectedIssueTypeFields = selectedIssueTypeFields.map((field: IAzureFieldType) => {
				if (field.referenceName === 'System.AreaPath') {
					return { ...field, allowedValues: areas, type: 'select' };
				}
				if (field.referenceName === 'System.IterationPath') {
					return { ...field, allowedValues: iterations, type: 'select' };
				}
				return fields[field.referenceName]?.isIdentity ? { ...field, allowedValues: users } : field;
			});

			return getFormattedFields(fields, selectedIssueTypeFields);
		} catch (error) {
			this.logError('getProjectFields', error);
			return {};
		}
	}

	private async getUser(): Promise<IAzureUser> {
		try {
			const response = await this.innerCallApi<AzureUserProfileResponse>(USER);
			const user = {
				id: response.id,
				email: response.emailAddress,
				displayName: response.displayName
			};
			return user;
		} catch (error) {
			this.logError('getUser', error);
			return {
				id: '',
				email: '',
				displayName: ''
			};
		}
	}

	public async getOrganizations(): Promise<OptionType[]> {
		try {
			const { id } = await this.getUser();
			const accountsUrl = new URL(ACCOUNTS_URL);
			accountsUrl.searchParams.set('memberId', id);
			accountsUrl.searchParams.set('api-version', ACCOUNTS_API_VERSION);
			const accounts = await this.innerCallApi<AzureListResponse<IAzureOrganization>>(
				accountsUrl.href
			);
			return accounts.value.map((organization) => {
				const paths = getPaths(organization.accountUri);
				const organizationName = paths[paths.length - 2];
				return { label: organizationName, value: organizationName };
			});
		} catch (error) {
			this.logError('getOrganizations', error);
			return null;
		}
	}

	public async getProjectTypes(host: string): Promise<OptionType[]> {
		const url: string = getProjectNameUrl(host, this.apiBaseUrl);
		try {
			const requestUrl = appendAzurePath(url, PROJECT_TYPES);
			mergeApiVersionParams(requestUrl);
			const { value } = await this.innerCallApi<AzureListResponse<IAzureProjectType>>(
				requestUrl.href
			);
			return getProjectTypesOptions(value) ?? null;
		} catch (error) {
			this.logError('getProjectTypes', error);
			return [];
		}
	}

	private openAzureTicket(url: string): void {
		openUrlInNewTab(url);
	}

	public async createIssue(
		host: string,
		selectedIssueType: string,
		screenshotUrlBase64: string,
		fields: IAzureFields,
		bypassProcessRules = true
	): Promise<boolean> {
		const attachmentHost: string = getProjectNameUrl(host, this.apiBaseUrl);

		try {
			let payload = getFieldsMetaData(fields);

			if (screenshotUrlBase64) {
				const attachmentFile = dataURItoBlob(screenshotUrlBase64);
				const attachmentUrl = appendAzurePath(attachmentHost, ATTACHMENT);
				mergeApiVersionParams(attachmentUrl);
				const attachment = await this.innerCallApi<AzureAttachmentResponse>(
					attachmentUrl.href,
					'POST',
					attachmentFile,
					true,
					'application/octet-stream'
				);

				payload = addAttachmentToPayload(attachment.url, payload);
			}
			const workItemUrl = appendAzurePath(
				attachmentHost,
				getAzureWorkItemCreateRelativePath(API_VERSION, selectedIssueType, bypassProcessRules)
			);
			const response = await this.innerCallApi<AzureWorkItemResponse>(
				workItemUrl.href,
				'POST',
				payload
			);

			if (response?._links?.html?.href) {
				this.openAzureTicket(response._links.html.href);
			}
			return !!response;
		} catch (error) {
			this.logError('createIssue', error);
			return false;
		}
	}
}

/** Build an {@link AzureApi} instance with its auth client and logger injected. */
export const createAzureApi = (deps: AzureApiDeps): AzureApi => new AzureApi(deps);

export { appendAzurePath, getAzureWorkItemCreateRelativePath, mergeApiVersionParams };
